import { InfoCircleTwoTone } from "@ant-design/icons";
import { assertNever } from "@p0security/frontend-core";
import { USAGE_LOOKBACK_DAYS } from "@p0security/shared/assessment/constants";
import { CredentialConsumer } from "@p0security/shared/types/assessment/data/base";
import { List, Typography } from "@p0security/ui";
import { DAYS, SECONDS } from "@p0security/util/time";
import { ScopeContext } from "components/Assessment/contexts/ScopeContext";
import { GraphTooltip } from "components/GraphTooltip";
import { ConditionalVirtualList } from "components/common/ConditionalVirtualList";
import moment from "moment";
import { useCallback, useMemo } from "react";
import { useContext } from "react";
import { Link } from "react-router-dom";

import { IdentityCell, IdentityLink } from "./Identity";

const { Paragraph, Text } = Typography;

const ConsumerHelpToolTip: React.FC = () => {
  const { provider } = useContext(ScopeContext);
  return (
    <>
      <Paragraph>
        Possible reasons for the unknown usage of this credential include:
      </Paragraph>
      <ul>
        {provider === "gcp" && (
          <li>
            The Access logs Google Cloud integration is not installed or was
            installed too recently to record usage.
          </li>
        )}
        <li>
          The usage occurred in a different project than the one the credential
          is located in.
        </li>
        <li>
          The credential was used for authentication, but no actions were
          performed after authenticating with the credential.
        </li>
      </ul>
    </>
  );
};

export const toConsumerText = (consumer: CredentialConsumer): string =>
  consumer.type === "ip"
    ? `${consumer.label} ${consumer.organization}`
    : consumer.type == "principal"
      ? consumer.label
      : assertNever(consumer);

export const ConsumersList: React.FC<{
  consumers: CredentialConsumer[] | undefined;
  lastAuthnTime?: number;
}> = ({ consumers, lastAuthnTime }) => {
  // Assumes frontend is reloaded at least once a day
  const maxLookbackTime = useMemo(
    () => Date.now() - USAGE_LOOKBACK_DAYS * DAYS,
    []
  );

  const sortedConsumers = useMemo(() => {
    return consumers?.sort((a, b) => {
      return b.lastUsedTime - a.lastUsedTime;
    });
  }, [consumers]);

  const itemRenderer = useCallback((c: CredentialConsumer, ix: number) => {
    return (
      <List.Item key={ix}>
        {c.type === "ip" ? (
          <>
            {c.userAgents?.length ? (
              <>
                <GraphTooltip
                  title={
                    <div>
                      User Agents: <br /> {c.userAgents.slice(0, 3).join(", ")}{" "}
                      {c.userAgents.length > 3
                        ? `(and ${c.userAgents.length - 3} others)`
                        : null}
                    </div>
                  }
                >
                  <InfoCircleTwoTone />
                </GraphTooltip>
                &nbsp;
              </>
            ) : null}
            <Link
              to={`https://www.geolocation.com/en_us?ip=${c.label}`}
              target="_blank"
            >
              {toConsumerText(c)}
            </Link>
          </>
        ) : c.type === "principal" ? (
          <>
            <IdentityLink
              data={{ ...c, type: c.identityType }}
              type="short"
              show="grant"
            />
            &nbsp;
            <IdentityCell identity={{ ...c, type: c.identityType }} />
          </>
        ) : (
          assertNever(c)
        )}
        &nbsp; (most recently on{" "}
        {moment.unix(c.lastUsedTime / SECONDS).format("LL")})
      </List.Item>
    );
  }, []);

  return sortedConsumers?.length ? (
    <ConditionalVirtualList<CredentialConsumer>
      data={sortedConsumers}
      itemKey="label"
      renderItem={itemRenderer}
    />
  ) : // We only load the last 90 days of consumers
  lastAuthnTime && lastAuthnTime < maxLookbackTime ? (
    <Text type="secondary">
      (No consumers in last {USAGE_LOOKBACK_DAYS} days)
    </Text>
  ) : (
    <GraphTooltip title={<ConsumerHelpToolTip />}>
      <Text type="secondary">(Unknown)</Text>
    </GraphTooltip>
  );
};
